import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { fetchTotalCount } from '../../../service/api/accountService';
import useSelectedDomain from '../../../service/Hooks/useSelectedDomain';
import { formatAmount } from '../../../service/utils/orderUtils';

const TotalCount = ({ refreshKey, isOffline, loading: parentLoading }) => {
    const [totalCount, setTotalCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const selectedDomain = useSelectedDomain();

    useEffect(() => {
        if (!selectedDomain?.id || isOffline) {
            setLoading(false);
            return;
        }
        
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const count = await fetchTotalCount(selectedDomain.id);
                setTotalCount(count);
            } catch (err) {
                console.error("Erreur TotalCount:", err);
                setError(err);
                setTotalCount(null);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [selectedDomain, refreshKey, isOffline]);

    //  Prioriser le loading parent si présent
    const isLoading = parentLoading || loading;

    const displayValue = isOffline ? '--' : 
                        error ? '--' : 
                        isLoading ? null : 
                        formatAmount(totalCount || 0);

    return (
        <div className={`card counter modern-card total-card ${isOffline || error ? 'offline-mode' : ''}`}>
            <div className="card-glow"></div>
            <div className="card-background"></div>
            
            {/* Indicateur offline */}
            {isOffline && (
                <div className="offline-indicator">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.58 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88m4.95-1.21a6 6 0 0 1 9.45 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"></path>
                    </svg>
                    <span>Hors ligne</span>
                </div>
            )}
            
            <div className="card-body text-center">
                {/* Icône */}
                <div className="card-element-spacing">
                    <div className="card-box-icon">
                        <div className="icon-shine"></div>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="url(#total-gradient)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="url(#total-gradient)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="url(#total-gradient)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="url(#total-gradient)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <defs>
                                <linearGradient id="total-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#6B7280" />
                                    <stop offset="100%" stopColor="#9CA3AF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>
                
                {/* Label */}
                <div className="card-element-spacing">
                    <p className="card-label">
                        <span className="label-text text-uppercase">Total comptes</span>
                        <span className="label-dot"></span>
                    </p>
                </div>
                
                {/* Compteur */}
                <div className="card-element-spacing">
                    {/*  SKELETON AU LIEU DE RIEN */}
                    {isLoading ? (
                        <div className="skeleton-count-inline" style={{
                            height: '32px',
                            width: '80px',
                            margin: '0 auto',
                            borderRadius: '8px',
                            background: 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.1) 100%)',
                            backgroundSize: '200% 100%',
                            animation: 'shimmer 1.5s ease-in-out infinite'
                        }} />
                    ) : (
                        <h2 className="card-count">
                            <span className={`count-number total-number glow-text ${isOffline || error ? 'text-muted' : ''}`}>
                                {displayValue}
                            </span>
                        </h2>
                    )}
                </div>
                
                {/* Sous-titre */}
                <div className="card-element-spacing">
                    <div className="card-subtitle">
                        <span className="subtitle-icon"></span>
                        {isOffline ? 'Connexion interrompue' : 
                         error ? 'Données temporairement indisponibles' : 
                         'Tous types confondus'}
                    </div>
                </div>
            </div>
            <div className="card-corner"></div>

            <style>{`
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }

                .skeleton-count-inline {
                    display: inline-block;
                }
            `}</style>
        </div>
    );
};

TotalCount.propTypes = {
    refreshKey: PropTypes.number,
    isOffline: PropTypes.bool,
    loading: PropTypes.bool //  NOUVEAU
};

export default TotalCount;